import { useGridRootProps } from "@components/DataGrid/hooks/useGridRootProps";
import SettingsIcon from "@mui/icons-material/Settings";
import { MenuList } from "@mui/material";
import { ButtonProps } from "@mui/material/Button";
import { TooltipProps } from "@mui/material/Tooltip";
import { unstable_useForkRef as useForkRef, unstable_useId as useId } from "@mui/utils";
import { gridClasses, GridMenu, useGridApiContext } from "@mui/x-data-grid-premium";
import { forwardRef } from "@mui/x-internals/forwardRef";
import PropTypes from "prop-types";
import * as React from "react";
import { isHideMenuKey } from "../../utils/keyboardUtils";

interface GridToolbarOptionsContainerProps {
  /**
   * The props used for each slot inside.
   * @default {}
   */
  slotProps?: { button?: Partial<ButtonProps>; tooltip?: Partial<TooltipProps> };
}

const GridToolbarOptionsContainer = forwardRef<HTMLButtonElement, React.PropsWithChildren<GridToolbarOptionsContainerProps>>((props, ref) => {
  const { children, slotProps = {} } = props;
  const buttonProps = slotProps.button || {};
  const tooltipProps = slotProps.tooltip || {};

  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();
  const optionsButtonId = useId();
  const optionsMenuId = useId();

  const [open, setOpen] = React.useState(false);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const handleRef = useForkRef(ref, buttonRef);

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setOpen((prevOpen) => !prevOpen);
    buttonProps.onClick?.(event);
  };

  const handleMenuClose = () => setOpen(false);

  const handleListKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Tab") {
      event.preventDefault();
    }
    if (isHideMenuKey(event.key)) {
      handleMenuClose();
    }
  };

  if (children == null) {
    return null;
  }

  return (
    <React.Fragment>
      <rootProps.slots.baseTooltip title={apiRef.current.getLocaleText("toolbarOptionsLabel" as any)} enterDelay={1000} {...rootProps.slotProps?.baseTooltip} {...tooltipProps}>
        <rootProps.slots.baseIconButton
          size="small"
          color="primary"
          aria-expanded={open}
          aria-label={apiRef.current.getLocaleText("toolbarOptionsLabel" as any)}
          aria-haspopup="menu"
          aria-controls={open ? optionsMenuId : undefined}
          id={optionsButtonId}
          {...rootProps.slotProps?.baseIconButton}
          {...buttonProps}
          onClick={handleMenuOpen}
          ref={handleRef}
        >
          <SettingsIcon fontSize="small" />
        </rootProps.slots.baseIconButton>
      </rootProps.slots.baseTooltip>
      <GridMenu open={open} target={buttonRef.current} onClose={handleMenuClose} position="bottom-start">
        <MenuList id={optionsMenuId} className={gridClasses.menuList} aria-labelledby={optionsButtonId} onKeyDown={handleListKeyDown} autoFocusItem={open}>
          {React.Children.map(children, (child) => {
            if (!React.isValidElement(child)) {
              return child;
            }
            return React.cloneElement<any>(child, { hideMenu: handleMenuClose });
          })}
        </MenuList>
      </GridMenu>
    </React.Fragment>
  );
});

GridToolbarOptionsContainer.propTypes = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  /**
   * The props used for each slot inside.
   * @default {}
   */
  slotProps: PropTypes.object,
} as any;

export { GridToolbarOptionsContainer };

